import { z } from 'zod';
import { posAttachmentListSchema } from './attachments';
import { activeDdtLineAmounts, posActiveDdtItemReadSchema } from './active-ddt';
import { resolvePosDdtDocumentSerials } from './ddt-identifiers';
import { posDdtOfferSchema } from './ddt-offers';
import { ddtCausaleLabel } from './ddt-causali';
import { documentIssuerSchema } from './document-issuer';
import { documentNumberingModeSchema } from './document-numbering';

const text = z.string().nullish();
const numeric = z.union([z.string(), z.number().finite()]).nullish();
const persistedCalendarDate = z.union([
  z.string().date(),
  z.string().datetime({ offset: true }),
  z.date(),
]).transform(value => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = value instanceof Date ? value : new Date(value);
  return parsed.toISOString().slice(0, 10);
}).nullish();

/** Shared read/print boundary. Only persisted documentary fields are projected;
 * joined catalog names/SKUs are deliberately not accepted as replacement data. */
export const posDdtPrintItemSchema = posActiveDdtItemReadSchema.partial().extend({
  id: z.string().optional(),
  product_id: text,
  variant_id: text,
  product_name: text,
  supplier_sku: text,
  ean: text,
  unit_of_measure: text,
  unit_price: numeric,
  quantity: numeric,
  discount_percent: numeric,
  vat_rate: numeric,
  supplier_name: text,
  offer_snapshot: posDdtOfferSchema.nullish(),
  serial_number: text,
  serial_numbers: z.array(z.string()).nullish(),
  lot_number: text,
  expiration_date: persistedCalendarDate,
  production_date: persistedCalendarDate,
  notes: text,
}).strip();

export const posDdtDocumentReadSchema = z.object({
  id: z.string(),
  company_id: z.string().optional(),
  issuer: documentIssuerSchema.optional(),
  ddt_number: z.string(),
  numbering_mode: documentNumberingModeSchema.optional(),
  ddt_date: z.union([z.string(), z.date(), z.null()]).transform(value =>
    value instanceof Date ? value.toISOString() : value ?? ''),
  status: z.string(),
  is_valorizzato: z.boolean().optional(),
  causale: text,
  customer_name: text,
  supplier_name: text,
  supplier_name_joined: text,
  supplier_vat: text,
  import_source: text,
  archived_at: text,
  warehouse_name: text,
  purchase_order_number: text,
  supplier_invoice_number: text,
  sale_number: text,
  notes: text,
  carrier_name: text,
  carrier_plate: text,
  transport_mode: text,
  appearance: text,
  packages: numeric,
  weight_kg: numeric,
  destination_address: text,
  destination_city: text,
  destination_province: text,
  destination_zip: text,
  /** RS emittente risolta (nome legale di `company_id`). */
  company_name: text,
  /**
   * Traccia origine→destinazione del DDT di trasferimento (causale
   * `trasferimento`): pratica, magazzino e RS di destinazione. Null sugli
   * altri DDT e sui trasferimenti storici emessi prima della traccia.
   */
  transfer_id: text,
  transfer_number: text,
  /** Vendita generata dalla conversione (DDT attivo): presente ⇒ non annullabile né riconvertibile. */
  sale_id: text,
  destination_warehouse_id: text,
  destination_warehouse_name: text,
  destination_company_id: text,
  destination_company_name: text,
  is_intercompany: z.boolean().nullable().optional(),
  purchase_orders: z.array(z.object({ id: z.string(), order_number: text })).optional(),
  supplier_invoices: z.array(z.object({ id: z.string(), document_number: text })).optional(),
  items: z.array(posDdtPrintItemSchema).default([]),
  attachments: posAttachmentListSchema.optional(),
});
export type PosDdtDocumentRead = z.infer<typeof posDdtDocumentReadSchema>;
export type PosDdtPrintItem = z.infer<typeof posDdtPrintItemSchema>;
export type DdtPrintCycle = 'attivi' | 'passivi';

function number(value: z.infer<typeof numeric>): number | null {
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
export function formatDdtPrintMoney(value: number | null): string {
  return value === null ? 'Non disponibile' : `€ ${value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
const decimal = (value: number | null) => value === null ? '—' : value.toLocaleString('it-IT', { maximumFractionDigits: 6 });

export function projectDdtPrint(source: PosDdtDocumentRead, cycle: DdtPrintCycle) {
  const valued = cycle === 'passivi' || source.is_valorizzato === true;
  const rows = source.items.map((item, index) => {
    const offer = cycle === 'attivi' ? item.offer_snapshot : null;
    const quantity = number(item.quantity);
    // An offer is authoritative even when a snapshot field is null.
    const price = number(offer ? offer.unit_price_net : item.unit_price);
    const rate = number(offer ? offer.vat_rate : item.vat_rate);
    const discount = number(offer ? offer.discount_percent : item.discount_percent) ?? 0;
    const calculated = activeDdtLineAmounts(quantity, price, rate, discount);
    const economics = valued ? {
      unitNet: price,
      unitGross: offer ? number(offer.unit_price_gross) : price === null || rate === null ? null : price * (1 + rate / 100),
      unitVat: offer ? number(offer.unit_vat_amount) : price === null || rate === null ? null : price * rate / 100,
      discount, rate,
      vatCode: offer ? offer.vat_code : item.vat_code,
      nature: offer ? offer.vat_nature : item.vat_nature,
      reference: offer ? offer.riferimento_normativo : item.riferimento_normativo,
      ...calculated,
    } : null;
    const supplierName = offer ? offer.supplier_name : item.supplier_name;
    const priceListName = offer ? `${offer.price_list_name} · versione ${offer.price_list_version}` : null;
    const attributes = item.variant_attributes ?? offer?.variant_attributes;
    const details: Array<{ label: string; value: string }> = [];
    const add = (label: string, value: string | null | undefined) => {
      if (value) details.push({ label, value });
    };
    add('Fornitore di approvvigionamento', supplierName);
    add('Listino', priceListName);
    if (offer) {
      add('Validità listino', [offer.valid_from ? `Dal ${offer.valid_from}` : null, offer.valid_to ? `Al ${offer.valid_to}` : null].filter(Boolean).join(' · '));
      if (valued && (offer.min_quantity !== null || offer.max_quantity !== null)) {
        add('Condizioni quantitative', [offer.min_quantity !== null ? `Minimo ${decimal(offer.min_quantity)}` : null, offer.max_quantity !== null ? `Massimo ${decimal(offer.max_quantity)}` : null].filter(Boolean).join(' · '));
      }
    }
    add('Variante', attributes ? Object.entries(attributes).map(([key, value]) => `${key}: ${value}`).join(' · ') : null);
    add('EAN', item.ean ?? offer?.ean);
    add('SKU interno (tecnico)', item.sku ?? offer?.sku);
    if (economics) {
      const kind = cycle === 'passivi' ? 'Costo' : 'Prezzo di vendita';
      add(`${kind} unitario netto / lordo pre-sconto`, `${formatDdtPrintMoney(economics.unitNet)} / ${formatDdtPrintMoney(economics.unitGross)}`);
      add('IVA unitaria pre-sconto', formatDdtPrintMoney(economics.unitVat));
      add('Sconto applicato una sola volta', `${decimal(discount)}%`);
      add('Imponibile riga post-sconto', formatDdtPrintMoney(economics.net));
      add('IVA aliquota / importo', `${decimal(rate)}% / ${formatDdtPrintMoney(economics.tax)}`);
      add('Codice IVA', economics.vatCode);
      add('Natura IVA', economics.nature);
      add('Riferimento normativo', economics.reference);
      add('Totale riga IVA inclusa', formatDdtPrintMoney(economics.total));
    }
    const serials = [...new Set([...(item.serial_numbers ?? []), ...(item.serial_number ? [item.serial_number] : [])])];
    resolvePosDdtDocumentSerials(item.document_serials, serials).forEach(serial =>
      add(serial.kind === null ? 'Seriale (tipo non documentato)' : serial.kind.toUpperCase(), serial.value));
    add('Lotto', item.lot_number);
    add('Data di scadenza', item.expiration_date);
    add('Data di produzione', item.production_date);
    add('Note riga', item.notes);
    return {
      key: item.id ?? `line-${index + 1}`, number: index + 1,
      supplierSku: (offer ? offer.supplier_sku : item.supplier_sku)
        || (cycle === 'attivi' && !valued ? '—' : 'SKU fornitore da associare'),
      description: item.product_name || offer?.product_name || '—',
      variant: attributes ? Object.values(attributes).join(' · ') : null,
      supplierName, priceListName, ean: item.ean ?? offer?.ean,
      expirationDate: item.expiration_date ?? null,
      productionDate: item.production_date ?? null,
      quantity, unit: item.unit_of_measure || '—', economics, details,
    };
  });
  const sum = (field: 'net' | 'tax' | 'total') => {
    let total = 0;
    for (const row of rows) {
      const value = row.economics?.[field];
      if (value == null) return null;
      total += value;
    }
    return total;
  };
  const net = sum('net');
  const tax = sum('tax');
  return {
    source, cycle, valued, rows,
    // Same canonical convention as conversion: accumulate net/tax separately,
    // then round at presentation. Never re-discount or split gross a second time.
    totals: valued ? { net, tax, total: net === null || tax === null ? null : net + tax } : null,
  };
}
export type DdtPrintProjection = ReturnType<typeof projectDdtPrint>;
export type DdtPrintRow = DdtPrintProjection['rows'][number];

const escape = (value: unknown) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** Bounded continuation rows allow long notes/serial lists to paginate rather
 * than enclosing an entire document line in an unbreakable table row. */
function chunks(value: string): string[] {
  const result: string[] = [];
  let remaining = value;
  while (remaining.length > 480) {
    const boundary = remaining.lastIndexOf(' ', 479);
    const end = boundary >= 200 ? boundary + 1 : 480;
    result.push(remaining.slice(0, end));
    remaining = remaining.slice(end);
  }
  if (remaining) result.push(remaining);
  return result;
}

export function renderDdtPrintHtml(document: DdtPrintProjection): string {
  const { source, valued, cycle, rows, totals } = document;
  const passive = cycle === 'passivi';
  const issuer = passive || (source.company_id && source.issuer?.company_id !== source.company_id)
    ? undefined
    : source.issuer;
  const money = (value: number | null) => escape(formatDdtPrintMoney(value));
  const field = (label: string, value: unknown) => value == null || value === '' ? '' : `<div><strong>${escape(label)}:</strong> ${escape(value)}</div>`;
  const safeLogo = issuer?.logo_data_uri
    && issuer.logo_data_uri.length <= 2_800_000
    && /^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(issuer.logo_data_uri)
    ? issuer.logo_data_uri
    : null;
  const issuerFiscal = issuer
    ? [
      issuer.vat_number ? `P. IVA ${issuer.vat_number}` : null,
      issuer.fiscal_code ? `C.F. ${issuer.fiscal_code}` : null,
    ].filter(Boolean).join(' · ')
    : '';
  const issuerContacts = issuer
    ? [
      issuer.email ? `Email ${issuer.email}` : null,
      issuer.pec ? `PEC ${issuer.pec}` : null,
      issuer.phone ? `Tel. ${issuer.phone}` : null,
    ].filter(Boolean).join(' · ')
    : '';
  const date = new Date(source.ddt_date);
  const dateLabel = Number.isFinite(date.getTime()) ? date.toLocaleDateString('it-IT') : source.ddt_date;
  const columns = valued ? 7 : 4;
  return `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8"><title>DDT ${escape(source.ddt_number)}</title>
<style>
@page{size:A4 portrait;margin:14mm 12mm}
*{box-sizing:border-box}body{font:11px/1.45 Arial,sans-serif;color:#17202b;margin:0 auto;max-width:186mm;padding:16px}
 h1{font-size:20px;margin:0}h2{font-size:12px;margin:0 0 5px}.header{border-bottom:2px solid #273747;padding-bottom:12px;margin-bottom:14px}.brand{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:10px}.brand-logo{display:block;max-width:48mm;max-height:22mm;width:auto;height:auto;object-fit:contain}.issuer{font-size:10px;line-height:1.35;text-align:right}.issuer strong{font-size:13px}.issuer div{overflow-wrap:anywhere}
.meta,.parties{display:flex;justify-content:space-between;gap:18px}.parties>section{flex:1;min-width:0}.parties{margin-bottom:16px}
table{border-collapse:collapse;width:100%;table-layout:fixed}thead{display:table-header-group}th,td{padding:6px;border-bottom:1px solid #ccd2d8;vertical-align:top;text-align:left;overflow-wrap:anywhere;white-space:pre-wrap}
th{font-size:10px;background:#eef1f4}.sku{width:${valued ? '17%' : '23%'}}.description{width:${valued ? '29%' : '49%'}}.qty{width:10%}.unit{width:8%}.amount{width:12%;text-align:right;font-variant-numeric:tabular-nums}
tr{break-inside:avoid;page-break-inside:avoid}.main{break-after:avoid;page-break-after:avoid}.detail td{font-size:10px;padding-top:3px;padding-bottom:3px;background:#fafbfc}.row-ref{color:#526173;font-size:9px}
.totals{margin:18px 0 0 auto;max-width:85mm;break-inside:avoid}.totals div{display:flex;justify-content:space-between;gap:12px;padding:4px}.grand{font-size:14px;border-top:2px solid #273747}
 .notes{white-space:pre-wrap;overflow-wrap:anywhere;margin:14px 0}.signatures{display:flex;justify-content:space-between;gap:14px;margin-top:36px;break-inside:avoid}.signatures div{border-top:1px solid #657180;padding-top:6px;flex:1;text-align:center}.issuer-footer{margin-top:16px;padding-top:7px;border-top:1px solid #ccd2d8;color:#526173;font-size:9px;text-align:center;overflow-wrap:anywhere;break-inside:avoid}
.convention{font-size:10px;color:#526173}section,header{overflow-wrap:anywhere}
@media print{body{padding:0;max-width:none}th,.detail td{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
</style></head><body>
 <header class="header">${issuer ? `<div class="brand">${safeLogo ? `<img class="brand-logo" src="${escape(safeLogo)}" alt="Logo ${escape(issuer.name)}">` : '<span></span>'}<div class="issuer"><strong>${escape(issuer.name)}</strong>${issuer.address_lines.map(line => `<div>${escape(line)}</div>`).join('')}${issuerFiscal ? `<div>${escape(issuerFiscal)}</div>` : ''}${issuerContacts ? `<div>${escape(issuerContacts)}</div>` : ''}</div></div>` : ''}<h1>${passive ? 'DDT PASSIVO — RIEPILOGO COSTI' : 'DOCUMENTO DI TRASPORTO'}</h1>
<div class="meta"><span>N. ${escape(source.ddt_number)} del ${escape(dateLabel)}</span><span>${escape(source.status)}</span></div></header>
<div class="parties"><section><h2>${passive ? 'Fornitore' : 'Destinatario'}</h2>
${field('Nome', passive ? source.supplier_name : source.customer_name)}
${field('Partita IVA', passive ? source.supplier_vat : null)}
${field('Indirizzo', source.destination_address)}
${field('Località', [source.destination_zip, source.destination_city, source.destination_province].filter(Boolean).join(' '))}
${field('Magazzino', source.warehouse_name)}
${field('Causale', source.causale ? ddtCausaleLabel(source.causale) : null)}
${field('Pratica trasferimento', source.transfer_number)}
${field('RS mittente', source.transfer_id ? source.company_name : null)}
${field('RS destinataria', source.destination_company_name)}
${field('Magazzino destinazione', source.destination_warehouse_name)}
${source.is_intercompany ? field('Intragruppo', 'Sì — trasferimento tra ragioni sociali diverse') : ''}
${field('Ordini', source.purchase_orders?.map(order => order.order_number).filter(Boolean).join(', ') || source.purchase_order_number)}
${field('Fatture fornitore', source.supplier_invoices?.map(invoice => invoice.document_number).filter(Boolean).join(', ') || source.supplier_invoice_number)}
${field('Vendita', source.sale_number)}</section><section><h2>Trasporto</h2>
${field('Modalità', source.transport_mode)}${field('Vettore', source.carrier_name)}${field('Targa', source.carrier_plate)}
${field('Aspetto', source.appearance)}${field('Colli', source.packages)}${field('Peso kg', source.weight_kg)}</section></div>
${valued ? `<p class="convention">${passive ? 'Costi di acquisto' : 'Prezzi di vendita'} unitari pre-sconto. Imponibile e totali riga post-sconto; lo sconto è applicato una sola volta. Importi arrotondati alla visualizzazione.</p>` : ''}
<table aria-label="Righe DDT"><thead><tr><th class="sku">SKU fornitore</th><th class="description">Descrizione articolo</th><th class="qty">Quantità</th><th class="unit">UdM</th>${valued ? `<th class="amount">${passive ? 'Costo netto' : 'Prezzo netto'}</th><th class="amount">IVA</th><th class="amount">Totale IVA inclusa</th>` : ''}</tr></thead><tbody>
${rows.map(row => `<tr class="main"><td>${escape(row.supplierSku)}</td><td><span class="row-ref">Riga ${row.number}</span><br>${escape(row.description.slice(0, 240))}${row.description.length > 240 ? ' (segue)' : ''}${row.variant ? `<br>${escape(row.variant.slice(0, 160))}${row.variant.length > 160 ? ' (vedi dettagli)' : ''}` : ''}</td><td>${escape(decimal(row.quantity))}</td><td>${escape(row.unit)}</td>${row.economics ? `<td class="amount">${money(row.economics.unitNet)}</td><td class="amount">${escape(decimal(row.economics.rate))}%</td><td class="amount">${money(row.economics.total)}</td>` : ''}</tr>
${[...(row.description.length > 240 ? [{ label: 'Descrizione articolo · continuazione', value: row.description.slice(240) }] : []), ...row.details].flatMap(detail => chunks(detail.value).map((part, index) => `<tr class="detail"><td colspan="${columns}"><span class="row-ref">Riga ${row.number}${index ? ' · continuazione' : ''}</span> — <strong>${escape(detail.label)}:</strong> ${escape(part)}</td></tr>`)).join('')}`).join('')}
</tbody></table>
${totals ? `<section class="totals"><div><strong>Imponibile documento</strong><span>${money(totals.net)}</span></div><div><strong>IVA documento</strong><span>${money(totals.tax)}</span></div><div class="grand"><strong>Totale documento</strong><span>${money(totals.total)}</span></div></section>` : ''}
${source.notes ? `<section class="notes"><h2>Note documento</h2>${escape(source.notes)}</section>` : ''}
<footer class="signatures"><div>Firma Mittente</div><div>Firma Vettore</div><div>Firma Destinatario</div></footer>
 ${issuer ? `<footer class="issuer-footer"><strong>${escape(issuer.name)}</strong>${issuer.address_lines.length ? ` · ${escape(issuer.address_lines.join(' · '))}` : ''}${issuerFiscal ? ` · ${escape(issuerFiscal)}` : ''}${issuerContacts ? ` · ${escape(issuerContacts)}` : ''}</footer>` : ''}
</body></html>`;
}
